import React, {
	Children,
	createContext,
	isValidElement,
	ReactElement,
	ReactNode,
	useContext,
	ElementType,
} from 'react';

import { ScrollTracking } from '../scroll-tracking/scroll-tracking';

interface ParentScrollProps {
	children: ReactElement | ReactNode;
	/**
	 * The pageId is used to identify the page in the tracking events.
	 * If omitted, it will try to find the first parent scroll tracking context and use that.
	 * Otherwise, the default value is "wac-web".
	 */
	pageId?: string;
	/**
	 * The scrollTrackingEnabled is used to enable or disable the scroll tracking for the children.
	 * If omitted, it will try to find the first parent scroll tracking context and use that.
	 * Otherwise, the default value is true.
	 */
	scrollTrackingEnabled?: boolean | undefined;

	/**
	 * The element used for each child passed to this component. Defaults to "div"
	 */
	as?: ElementType;
}

interface NamedComponent extends React.Component {
	displayName: string;
}

type Child =
	| string
	| number
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	| ReactElement<any, string | React.JSXElementConstructor<any>>
	| Iterable<ReactNode>
	| React.ReactPortal;

type ScrollTrackingContextType = Pick<ParentScrollProps, 'pageId' | 'scrollTrackingEnabled'>;

const ScrollTrackingContext = createContext<ScrollTrackingContextType>({
	pageId: 'wac-web',
	scrollTrackingEnabled: true,
});

export const useParentScrollTracking = () => {
	return useContext(ScrollTrackingContext);
};

export const ParentScrollTracking = (props: ParentScrollProps) => {
	const parentContext = useParentScrollTracking();
	const arrayChildren = Children.toArray(props.children);
	const definedName = (child: Child) => {
		if (isValidElement(child) && child.type) {
			// Use a type assertion to tell TypeScript that the type has a displayName
			const namedComponent = child.type as unknown as NamedComponent;
			return namedComponent.displayName;
		} else {
			return undefined;
		}
	};

	// Determine tracking of parent undefined means true
	const parentTrackScrolling =
		parentContext.scrollTrackingEnabled === undefined ? true : parentContext.scrollTrackingEnabled;

	// Parent should take precedence if parent tracking is disabled
	const trackScrolling =
		parentTrackScrolling &&
		(props.scrollTrackingEnabled === undefined ? true : props.scrollTrackingEnabled);

	const pageId = props.pageId ?? parentContext.pageId;

	return (
		<ScrollTrackingContext.Provider
			value={{
				pageId,
				scrollTrackingEnabled: trackScrolling,
			}}
		>
			{Children.map(arrayChildren, (child, index) => {
				const name = definedName(child);
				return name ? (
					<ScrollTracking
						key={index}
						index={`${index}/${arrayChildren.length}`}
						section={name}
						as={props.as}
					>
						{child}
					</ScrollTracking>
				) : (
					child
				);
			})}
		</ScrollTrackingContext.Provider>
	);
};
